import { useMemo } from "react";
import { DropDownMenu } from "../../../../../../shared/react/dropdown";
import { classes } from "../../../../../../shared/react/react";
import { getRoleName, roles } from "../../roles";
import { GenericProps, HoldingRole, EditorUser } from "../../types";
import { isArrayEmpty } from "@omnia/shared/utils/array";

interface Props extends GenericProps {
    user: EditorUser
}

export function UserRoleCell({ user, dispatch, state: { loggedInUser, api: { enableRestrictedMembers } } }: Props) {

    const { holdingRole, userId, deleted } = user

    const updated = holdingRole !== user.initialState.holdingRole

    const rolesArray = useAvailableRoles(user, enableRestrictedMembers, loggedInUser.ownerRole, loggedInUser.userId == userId)
    const roleName = getRoleName(holdingRole)

    // The role of the current user can be changed if:
    // - It has not been flagged as "deleted"
    // - There are roles available (rolesArray not empty)
    // - It is not the current user
    // - Or, if it is the current user, it is currently in the "member", in which case the only option
    // will be to switch to "restricted member"
    const canUpdate = (!deleted)
        && (!isArrayEmpty(rolesArray))
        && (
            (userId !== loggedInUser.userId)
            || (user.holdingRole == HoldingRole.Member)
        )

    const update = (role: HoldingRole) => {

        dispatch({ type: "updateUser", payload: { userId, role } })

        // When switching a user to "restricted member",
        // also turn off their access to the production module

        if (role === HoldingRole.RestrictedMember) {

            dispatch({
                type: "updatePermissions",
                payload: {
                    userId,
                    updates: [
                        {
                            key: "hub.production",
                            values: {}
                        }
                    ]
                }
            })

        }
    }


    return <td className={classes("holding-role", updated && "updated")}>

        <DropDownMenu
            disabled={!canUpdate}
            button={
                <button type="button" className="btn btn-link">
                    {roleName}
                </button>
            }
        >

            {
                rolesArray.map(r =>
                    <li role="menuitem" key={r.role}>
                        <a onClick={() => update(r.role)}>
                            {r.name}
                        </a>
                    </li>
                )
            }

        </DropDownMenu>

    </td>
}


function useAvailableRoles(user: EditorUser, enableRestrictedMembers: boolean, owner: boolean, self: boolean) {

    enableRestrictedMembers ||= user.holdingRole === HoldingRole.RestrictedMember

    return useMemo(() => {

        const rolesArray = Object.values(roles)

        if (!owner) {
            // If the current user is not an owner, then the option option
            // is to switch to "restricted member"
            if (!self) return []
            if (!enableRestrictedMembers) return []
            if (user.holdingRole === HoldingRole.RestrictedMember) return []
            return rolesArray.filter(r => r.role === HoldingRole.RestrictedMember)
        }

        return enableRestrictedMembers
            ? rolesArray
            : rolesArray.filter(r => r.role !== HoldingRole.RestrictedMember)

    }, [enableRestrictedMembers, owner, self])

}
